import React, { useState } from "react";
import { BookOpen, Globe, Brain, Copy } from "lucide-react";

const DeepResearchPanel = ({
  onResearch,
  isProcessing = false,
  // Research settings props
  researchMode = "simple",
  citationStyle = "APA",
  includeWebSources = true,
  includeDocSources = true,
  includeAIReasoning = true,
  onResearchModeChange,
  onCitationStyleChange,
  onIncludeWebSourcesChange,
  onIncludeDocSourcesChange,
  onIncludeAIReasoningChange,
  // Research results
  references = [],
  onSaveReference,
}) => {
  const [activeTab, setActiveTab] = useState("settings");

  const researchModes = [
    {
      value: "simple",
      label: "Simple Question",
      description: "Natural Q&A format",
    },
    {
      value: "research",
      label: "Research Prompt",
      description: "Structured, multi-source answer with citations",
    },
    {
      value: "deep",
      label: "Deep Dive",
      description: "Long-form report with sections and references",
    },
    {
      value: "compare",
      label: "Compare Mode",
      description: "Compare answers from different sources",
    },
  ];

  const citationStyles = [
    "APA",
    "MLA",
    "Chicago",
    "Harvard",
    "IEEE",
    "ACS",
    "AMA",
    "Vancouver",
    "CSE",
    "Bluebook",
    "OSCOLA",
  ];

  const handleCopyReference = (reference) => {
    navigator.clipboard.writeText(reference.url || reference.title);
  };

  const handleOpenReference = (reference) => {
    if (reference.url) {
      window.open(reference.url, "_blank");
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-50">
      {/* Tab Navigation */}
      <div className="p-4 pb-0">
        <div className="flex p-[2px] rounded-full bg-bg-60">
          <button
            onClick={() => setActiveTab("settings")}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-full transition-colors ${
              activeTab === "settings"
                ? "bg-ac-02 text-white dark:text-black/70 shadow-sm"
                : "text-fg-60 hover:text-fg-50 hover:bg-bg-40"
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab("references")}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-full transition-colors ${
              activeTab === "references"
                ? "bg-ac-02 text-white dark:text-black/70 shadow-sm"
                : "text-fg-60 hover:text-fg-50 hover:bg-bg-40"
            }`}
          >
            References
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin">
        {activeTab === "settings" && (
          <>
            {/* Research Mode */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-fg-50">
                Research Mode
              </label>
              <div className="space-y-2">
                {researchModes.map((mode) => (
                  <label
                    key={mode.value}
                    className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-bg-40 transition-colors"
                  >
                    <input
                      type="radio"
                      name="researchMode"
                      value={mode.value}
                      checked={researchMode === mode.value}
                      onChange={(e) => onResearchModeChange?.(e.target.value)}
                      className="text-ac-02 focus:ring-ac-02 mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-fg-50">
                        {mode.label}
                      </div>
                      <div className="text-xs text-fg-60 mt-1">
                        {mode.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Citation Style */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-fg-50">
                Citation Style
              </label>
              <select
                value={citationStyle}
                onChange={(e) => onCitationStyleChange?.(e.target.value)}
                className="w-full p-3 bg-bg-60 rounded-lg text-sm text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
              >
                {citationStyles.map((style) => (
                  <option key={style} value={style}>
                    {style}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Types */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-fg-50">
                Include Sources
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeWebSources}
                    onChange={(e) =>
                      onIncludeWebSourcesChange?.(e.target.checked)
                    }
                    className="text-ac-02 focus:ring-ac-02"
                  />
                  <Globe size={16} className="text-fg-60" />
                  <span className="text-sm text-fg-50">Web Sources</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDocSources}
                    onChange={(e) =>
                      onIncludeDocSourcesChange?.(e.target.checked)
                    }
                    className="text-ac-02 focus:ring-ac-02"
                  />
                  <BookOpen size={16} className="text-fg-60" />
                  <span className="text-sm text-fg-50">Document Sources</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAIReasoning}
                    onChange={(e) =>
                      onIncludeAIReasoningChange?.(e.target.checked)
                    }
                    className="text-ac-02 focus:ring-ac-02"
                  />
                  <Brain size={16} className="text-fg-60" />
                  <span className="text-sm text-fg-50">AI Reasoning</span>
                </label>
              </div>
            </div>

            {/* Processing Status */}
            {isProcessing && (
              <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-ac-02 border-t-transparent rounded-full animate-spin"></div>
                  <div className="text-sm text-fg-50">
                    Conducting deep research...
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "references" && (
          <>
            {/* References Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-fg-60" />
                <h3 className="text-sm font-medium text-fg-50">References</h3>
                <span className="text-xs text-fg-60 bg-bg-60 px-2 py-1 rounded-full">
                  {references.length}
                </span>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
                {references.length === 0 ? (
                  <div className="text-xs text-fg-60 text-center py-8">
                    No references yet. Start a research query to see sources.
                  </div>
                ) : (
                  references.map((ref, index) => (
                    <div
                      key={index}
                      className="bg-bg-60 rounded-lg p-3 hover:bg-bg-40 transition-colors cursor-pointer group"
                      onClick={() => handleOpenReference(ref)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Favicon */}
                        <div className="flex-shrink-0 mt-0.5">
                          <img
                            src={`https://www.google.com/s2/favicons?domain=${ref.source}&sz=16`}
                            alt=""
                            className="w-4 h-4 rounded-sm"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                          />
                          <div className="w-4 h-4 bg-bg-40 rounded-sm flex items-center justify-center hidden">
                            <Globe size={10} className="text-fg-60" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-fg-50 line-clamp-2 group-hover:text-ac-02 transition-colors">
                            {ref.title}
                          </div>
                          <div className="text-xs text-fg-60 mt-1">
                            {ref.source}
                          </div>
                          {ref.url && (
                            <div className="text-xs text-fg-70 mt-1 truncate">
                              {ref.url.length > 50
                                ? `${ref.url.substring(0, 50)}...`
                                : ref.url}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyReference(ref);
                            }}
                            className="p-1 text-fg-60 hover:text-fg-50 transition-colors"
                            title="Copy URL"
                          >
                            <Copy size={12} />
                          </button>
                          {ref.url && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenReference(ref);
                              }}
                              className="p-1 text-fg-60 hover:text-fg-50 transition-colors"
                              title="Open source"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15,3 21,3 21,9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeepResearchPanel;
